import React, {useState, useEffect, useMemo} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {useFinancial, getSortedAccounts} from '../../context/FinancialContext';

const {FiX, FiPlus, FiTrash2, FiChevronDown} = FiIcons;

const BulkEntryModal = ({isOpen, type, onClose, onSave, editItem = null}) => {
  const {state, dispatch} = useFinancial();
  const [entries, setEntries] = useState([]);
  const [customAccountName, setCustomAccountName] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(null);
  const isEditing = !!editItem;
  const availableAccounts = useMemo(() => {
    const sorted = getSortedAccounts(state.customAccounts);
    const category = type === 'income' ? 'income' : type === 'expense' ? 'drain' : 'investment';
    let list = sorted.filter(account => account.category === category);
    if (editItem && !list.some(account => account.id === editItem.accountType)) {
      const current = sorted.find(account => account.id === editItem.accountType);
      if (current) list = [...list, current];
    }
    return list;
  }, [type, state.customAccounts, editItem]);

  useEffect(() => {
    if (!isOpen || availableAccounts.length === 0) return;
    if (editItem) {
      setEntries([{
        id: editItem.id, name: editItem.name || '', amount: String(editItem.amount ?? ''),
        frequency: editItem.frequency || 'monthly', accountType: editItem.accountType || availableAccounts[0].id
      }]);
    } else {
      setEntries([{id: Date.now(), name: '', amount: '', frequency: 'monthly', accountType: availableAccounts[0].id}]);
    }
  }, [isOpen, availableAccounts, editItem]);

  const handleAddRow = () => setEntries([
    ...entries,
    {id: Date.now() + Math.random(), name: '', amount: '', frequency: 'monthly', accountType: availableAccounts[0].id}
  ]);
  const handleRemoveRow = id => {
    if (entries.length > 1) setEntries(entries.filter(entry => entry.id !== id));
  };
  const handleChange = (id, field, value) => {
    if (field === 'accountType' && value === 'CREATE_NEW') {
      setShowCustomInput(id);
      return;
    }
    setEntries(entries.map(entry => entry.id === id ? {...entry, [field]: value} : entry));
  };
  const handleCreateAccount = id => {
    if (!customAccountName.trim()) return;
    const accountId = customAccountName.toLowerCase().replace(/\s+/g, '_');
    const category = type === 'income' ? 'income' : type === 'expense' ? 'drain' : 'investment';
    dispatch({
      type: 'ADD_CUSTOM_ACCOUNT',
      payload: {
        id: accountId, name: customAccountName,
        type: type === 'income' ? 'Income' : type === 'expense' ? 'Drain' : 'Investment',
        category, purpose: 'User defined custom route.'
      }
    });
    setEntries(entries.map(entry => entry.id === id ? {...entry, accountType: accountId} : entry));
    setCustomAccountName('');
    setShowCustomInput(null);
  };
  const handleSave = () => {
    onSave(entries.filter(entry => entry.name && entry.amount !== '' && entry.amount !== null));
  };
  if (!isOpen) return null;
  const config = {
    income: {title: 'Revenue', buttonClass: 'bg-emerald-600'},
    expense: {title: 'Drain', buttonClass: 'bg-rose-600'},
    investment: {title: 'Investment', buttonClass: 'bg-blue-600'}
  };
  const currentConfig = config[type] || config.investment;
  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <motion.div initial={{opacity: 0, scale: 0.95}} animate={{opacity: 1, scale: 1}} className="bg-white w-full max-w-5xl rounded-[2rem] shadow-2xl relative flex flex-col max-h-[90vh]">
        <div className="p-6 md:p-8 pb-4 flex justify-between items-start border-b border-slate-100">
          <div>
            <h3 className="text-xl md:text-2xl font-black text-slate-900">{isEditing ? `Edit ${currentConfig.title} Entry` : `Bulk ${currentConfig.title} Entry`}</h3>
            <p className="text-slate-500 font-bold text-[10px] uppercase tracking-widest mt-1">{isEditing ? 'Update the details for this item' : `Select or create routing for your ${currentConfig.title.toLowerCase()} flows`}</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-full transition-all"><SafeIcon icon={FiX} className="text-xl" /></button>
        </div>
        <div className="p-4 md:p-8 overflow-y-auto custom-scrollbar flex-1 space-y-4">
          {entries.map(entry => (
            <div key={entry.id} className="relative p-5 md:p-6 bg-slate-50 rounded-2xl border border-slate-100/50 group pt-8 md:pt-6">
              {entries.length > 1 && (
                <button onClick={() => handleRemoveRow(entry.id)} className="absolute top-3 right-3 p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all" title="Remove entry">
                  <SafeIcon icon={FiTrash2} className="text-[14px]" />
                </button>
              )}
              <div className="grid grid-cols-2 md:grid-cols-12 gap-4 items-end">
                <div className="col-span-2 md:col-span-4 space-y-1.5 min-w-0">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Description</label>
                  <input className="w-full px-4 py-3 bg-white rounded-xl font-bold text-sm text-slate-900 border-none outline-none focus:ring-2 focus:ring-blue-500 shadow-sm" value={entry.name} onChange={event => handleChange(entry.id, 'name', event.target.value)} placeholder="e.g. Consulting Fee" />
                </div>
                <div className="col-span-1 md:col-span-2 space-y-1.5 min-w-0">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-black text-sm">$</span>
                    <input type="number" className="w-full pl-7 pr-3 py-3 bg-white rounded-xl font-bold text-sm text-slate-900 border-none outline-none focus:ring-2 focus:ring-blue-500 shadow-sm" value={entry.amount} onChange={event => handleChange(entry.id, 'amount', event.target.value)} placeholder="0" />
                  </div>
                </div>
                <div className="col-span-1 md:col-span-2 space-y-1.5 min-w-0">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Period</label>
                  <div className="relative">
                    <select className="w-full appearance-none px-3 py-3 bg-white rounded-xl font-bold text-sm text-slate-900 border-none outline-none focus:ring-2 focus:ring-blue-500 shadow-sm cursor-pointer pr-8" value={entry.frequency} onChange={event => handleChange(entry.id, 'frequency', event.target.value)}>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400"><SafeIcon icon={FiChevronDown} /></div>
                  </div>
                </div>
                <div className="col-span-2 md:col-span-4 space-y-1.5 min-w-0">
                  <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Account Route</label>
                  {showCustomInput === entry.id ? (
                    <div className="flex items-center gap-2 w-full min-w-0">
                      <input autoFocus className="min-w-0 w-0 flex-1 px-3 py-3 bg-white rounded-xl font-bold text-sm text-slate-900 border-2 border-blue-500 outline-none shadow-sm" value={customAccountName} onChange={event => setCustomAccountName(event.target.value)} placeholder="Enter account name..." onKeyDown={event => event.key === 'Enter' && handleCreateAccount(entry.id)} />
                      <button onClick={() => handleCreateAccount(entry.id)} aria-label="Create account route" className="flex-none w-9 h-11 bg-blue-600 text-white rounded-xl flex items-center justify-center"><SafeIcon icon={FiPlus} /></button>
                      <button onClick={() => setShowCustomInput(null)} aria-label="Cancel new account route" className="flex-none w-9 h-11 bg-slate-200 text-slate-600 rounded-xl flex items-center justify-center"><SafeIcon icon={FiX} /></button>
                    </div>
                  ) : (
                    <div className="relative">
                      <select className="w-full appearance-none px-4 py-3 bg-white rounded-xl font-bold text-sm text-slate-900 border-none outline-none focus:ring-2 focus:ring-blue-500 shadow-sm cursor-pointer pr-10" value={entry.accountType} onChange={event => handleChange(entry.id, 'accountType', event.target.value)}>
                        {availableAccounts.map(account => <option key={account.id} value={account.id}>{account.name}</option>)}
                        <option value="CREATE_NEW" className="text-blue-600 font-black">+ Create Custom...</option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400"><SafeIcon icon={FiChevronDown} /></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 md:p-8 pt-4 border-t border-slate-100 flex flex-col md:flex-row gap-3 md:gap-4 bg-white rounded-b-[2rem]">
          {!isEditing && <button onClick={handleAddRow} className="w-full md:flex-1 py-4 border-2 border-dashed border-slate-200 text-slate-400 hover:border-slate-300 hover:bg-slate-50 rounded-xl font-bold flex items-center justify-center gap-2 transition-all"><SafeIcon icon={FiPlus} /> Add Another Entry</button>}
          <button onClick={handleSave} className={`w-full ${isEditing ? '' : 'md:flex-[2]'} py-4 text-white rounded-xl font-black shadow-lg transition-all ${currentConfig.buttonClass}`}>{isEditing ? 'Save Changes' : `Save All ${entries.length} Entries`}</button>
        </div>
      </motion.div>
    </div>
  );
};

export default BulkEntryModal;